"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { gsap } from "gsap";

export default function LoadingAnimation() {
  const imagesCount = 20;
  const imageRefs = useRef(
    new Array(imagesCount).fill(null).map(() => React.createRef()),
  );
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const preloadImages = useCallback(() => {
    const imagePromises = Array.from(
      { length: imagesCount },
      (_, i) =>
        new Promise((resolve) => {
          const img = new Image();
          img.src = `/assets/games/spin-wheel/animations/loading/wheel-${i + 1}.svg`;
          img.onload = resolve; // Resolve the promise when the image is loaded
        }),
    );

    Promise.all(imagePromises).then(() => setImagesLoaded(true)); // Set loaded state true when all images are loaded
  }, [imagesCount]);

  useEffect(() => {
    preloadImages();
  }, []);

  useEffect((): (() => void) | void => {
    if (!imagesLoaded) return;

    gsap.set(
      imageRefs.current.map((ref) => ref.current as EventTarget), // Provide the correct type for ref.current
      { autoAlpha: 0 },
    ); // Initially hide all images

    const tl = gsap.timeline({
      repeat: -1, // Loop indefinitely
      defaults: { immediateRender: false },
    });

    imageRefs.current.forEach((ref) => {
      tl.set(ref.current as EventTarget, { autoAlpha: 1 }) // Show current image
        .set(ref.current as EventTarget, { autoAlpha: 0 }, `+=0.1`); // Hide current image after 0.5 seconds
    });

    return () => tl.kill();
  }, [imagesLoaded]);

  const loadingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleResize() {
      if (!loadingRef.current) return;

      let scaleFactor = loadingRef.current.offsetWidth / 200; // Access the offsetWidth property of loadingRef.current
      if (scaleFactor > 1) scaleFactor = 1;
      loadingRef.current.style.transform = `scale(${scaleFactor})`;
    }

    window.addEventListener("resize", handleResize);
    handleResize(); // Initial scaling

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      <div className="h-full w-full" ref={loadingRef}>
        {Array.from({ length: imagesCount }, (_, i) => (
          <img
            key={i}
            ref={imageRefs.current[i] as React.RefObject<HTMLImageElement>}
            src={`/assets/games/spin-wheel/animations/loading/wheel-${i + 1}.svg`}
            alt={`Frame ${i + 1}`}
            className="h-full w-full"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              visibility: imagesLoaded ? "visible" : "hidden",
            }}
          />
        ))}
      </div>
    </>
  );
}
